import { FC, useEffect } from 'react';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Card, Container, useScrollTrigger } from '@mui/material';
import { dateRangeTypeOptions, getDisablingDates } from 'entities/analytics';
import { Col } from 'shared/ui/Col';
import { Row } from 'shared/ui/Row';
import { SelectField } from 'shared/ui/SelectField';
import { StyledDatePicker } from 'shared/ui/StyledDatePicker';
// import { MarketAnalyticsCard } from './AnalyticsCards/MarketAnalyticsCard';
import { ObjectAnalyticsCard } from './AnalyticsCards/ObjectAnalyticsCard';

export const formatDateToString = (date: Date | string | undefined) =>
  date ? dayjs(date).format('DD-MM-YYYY') : '';

const today = dayjs().endOf('day');
const defaultStartDate = today.subtract(1, 'week').add(1, 'day');

export const AnalyticsTab: FC = () => {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: {
      dateRangeType: dateRangeTypeOptions[2].value,
      dateRangeStart: defaultStartDate.toDate(),
      dateRangeEnd: today.toDate(),
    },
  });

  const { watch, setValue } = form;
  const dateRangeStart = watch('dateRangeStart');
  const dateRangeEnd = watch('dateRangeEnd');
  const selectedDateRangeType = watch('dateRangeType');

  useEffect(() => {
    if (!selectedDateRangeType) return;
    const today = dayjs().endOf('day');
    let startDate = today;
    let endDate = today;

    switch (selectedDateRangeType) {
      case 1:
        startDate = today;
        break;
      case 2:
        startDate = today.subtract(1, 'day');
        endDate = startDate;
        break;
      case 3:
        startDate = today.subtract(1, 'week').add(1, 'day');
        break;
      case 4:
        startDate = today.subtract(1, 'month').add(1, 'day');
        break;
      case 5: {
        startDate = today.subtract(3, 'month').add(1, 'day');
        break;
      }
      case 6:
        startDate = today.subtract(1, 'year').add(1, 'day');
        break;
    }

    setValue('dateRangeStart', startDate.toDate());
    setValue('dateRangeEnd', endDate.toDate());
  }, [selectedDateRangeType, setValue]);

  const { shouldDisableDate, shouldDisableMonth, shouldDisableYear } =
    getDisablingDates({ maxDate: dateRangeEnd });
  const {
    shouldDisableDate: shouldDisableEndDate,
    shouldDisableMonth: shouldDisableEndMonth,
    shouldDisableYear: shouldDisableEndYear,
  } = getDisablingDates({ minDate: dateRangeStart });

  const resetDateRange = () => {
    form.setValue('dateRangeType', '');
  };

  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 400,
  });

  return (
    <Col spacing={8} sx={{ mt: scrollTrigger ? '82px' : 0 }}>
      <Container>
        <Card
          sx={{
            width: 'fit-content',
            p: 5,
            position: scrollTrigger ? 'fixed' : 'static',
            top: scrollTrigger ? 24 : 'auto',
            zIndex: scrollTrigger ? 100 : 1,
          }}
        >
          <FormProvider {...form}>
            <Row spacing={5}>
              <SelectField
                options={dateRangeTypeOptions}
                label={t('analytics.filter.label.period')}
                name={'dateRangeType'}
                sx={{ width: 160 }}
              />
              <StyledDatePicker
                label={t('analytics.filter.label.periodStart')}
                name="dateRangeStart"
                onChange={resetDateRange}
                sx={{ width: 240 }}
                shouldDisableDate={shouldDisableDate}
                shouldDisableMonth={shouldDisableMonth}
                shouldDisableYear={shouldDisableYear}
              />

              <StyledDatePicker
                label={t('analytics.filter.label.periodEnd')}
                name="dateRangeEnd"
                onChange={resetDateRange}
                sx={{ width: 240 }}
                shouldDisableDate={shouldDisableEndDate}
                shouldDisableMonth={shouldDisableEndMonth}
                shouldDisableYear={shouldDisableEndYear}
              />
            </Row>
          </FormProvider>
        </Card>
      </Container>

      <Col
        spacing={9}
        sx={{ maxWidth: 1400, m: 'auto' }}
        //  sx={{ width: '1343px', ml: '-159.5px' }}
      >
        <ObjectAnalyticsCard
          dateRangeStart={formatDateToString(dateRangeStart)}
          dateRangeEnd={formatDateToString(dateRangeEnd)}
        />
        {/* <MarketAnalyticsCard /> */}
      </Col>
    </Col>
  );
};
